import { useEffect, useRef, useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';
import { differenceInHours } from 'date-fns';
import { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import { SaveClientFlags } from '@sdk/authentication/types';
import useModalState from '@sdk/components/ui/modal/useModalState';
import Modal from '@sdk/components/ui/modal/Modal';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import gettext from '@sdk/lang/gettext';
import H3 from '@sdk/components/ui/typography/H3';
import Text16 from '@sdk/components/ui/typography/Text16';
import ProgressButton from '@sdk/components/ui/buttons/ProgressButton';
import { ButtonType } from '@sdk/components/ui/buttons/ButtonType';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import PhoneFormField from '@sdk/login/components/fields/PhoneFormField';
import { DEFAULT_COUNTRY_CODE } from '@sdk/login/hooks/useCountryCode';
import getCountryCode from '@sdk/login/helpers/getCountryCode';
import Clickable from '@sdk/components/primitives/Clickable';
import Text14 from '@sdk/components/ui/typography/Text14';
import useSlingApi from '@sdk/api/useSlingApi';
import { SmsType } from '@sdk/users/types/SmsType';

import PhoneImg from '../../assets/images/iPhoneX_mobile.png';

const Container = styled(Row)`
  max-width: 756px;
  min-height: 456px;
  flex-shrink: 1;
  box-sizing: border-box;
`;

const LeftColumn = styled(Column)`
  flex-shrink: 1;
  max-width: 306px;
  min-height: 396px;
`;

const StyledImg = styled.img`
  width: 306px;
  height: 396px;
`;

const RightColumn = styled(Column)`
  flex-shrink: 1;
`;

const SkipButton = styled(Clickable)`
  opacity: 0.65;
  align-self: center;
`;

type PhoneData = {
  phone: string;
  countryCode: string;
  countryISOCode: string;
};

type Props = {
  user: AuthUserType;
  forceDisplay?: boolean;
  saveClientFlags: SaveClientFlags;
  onClose: () => void;
  updatePhone: ({ phoneData }: { phoneData: PhoneData }) => Promise<void>;
};

const GetMobileAppPopup: React.FC<React.PropsWithChildren<Props>> = ({
  user,
  forceDisplay,
  saveClientFlags,
  onClose,
  updatePhone,
}) => {
  const wasDisplayedRef = useRef(false);
  const clientFlags = user?.settings?.clientFlags;
  const { isVisible, open: openModal, close: closeModal } = useModalState();
  const config = useRef({ noDismiss: true }).current;
  const [phoneNumber, setPhoneNumber] = useState(user.phone);
  const [countryCode, setCountryCode] = useState(
    () => getCountryCode(user.countryCode, user.countryISOCode) || DEFAULT_COUNTRY_CODE
  );
  const [serverError, setServerError] = useState('');
  const phoneFieldRef = useRef(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const api = useSlingApi();

  const shouldDisplay = (): boolean => {
    if (!clientFlags?.canShowMobileAppPopup) {
      return false;
    }

    if (clientFlags?.hadMobileSession) {
      return false;
    }

    const hoursPassedFromOrgCreate = differenceInHours(new Date(), new Date(user.org.createdAt));

    if (hoursPassedFromOrgCreate < 24) {
      return false;
    }

    return true;
  };

  const close = (): void => {
    onClose?.();
    closeModal();
    saveClientFlags({ flags: { canShowMobileAppPopup: false } });
  };

  useEffect(() => {
    if (forceDisplay || (!wasDisplayedRef.current && shouldDisplay())) {
      openModal();
      wasDisplayedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceDisplay]);

  const submit = async (): Promise<string> => {
    setServerError('');
    setIsSubmitted(true);

    if (!phoneNumber) {
      setServerError(gettext('Please provide phone number'));
      return Promise.reject();
    }

    if (!phoneFieldRef.current.validate()) {
      return Promise.reject();
    }

    try {
      if (
        phoneNumber !== user.phone ||
        countryCode.value !== user.countryCode ||
        user.countryISOCode !== countryCode.code
      ) {
        await updatePhone({
          phoneData: {
            phone: phoneNumber,
            countryCode: countryCode.value,
            countryISOCode: countryCode.code,
          },
        });
      }

      await api.users.sendSms(user.id, SmsType.mobileDownload);
      close();
    } catch (e) {
      const message = e.message || gettext('Server error');
      setServerError(message);
      return Promise.reject();
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Modal fixed config={config} isVisible={isVisible} onDismiss={close}>
      <Container mLeft={70} mRight={100}>
        <LeftColumn mTop={60}>
          <StyledImg src={PhoneImg} />
        </LeftColumn>
        <RightColumn mLeft={100} mTop={90}>
          <H3 mBottom={10}>{gettext('Get the mobile app')}</H3>
          <Text16 themeColor={ThemeColorType.gray} mBottom={20}>
            {gettext(
              'Download the Sling app to stay connected to your team wherever you are. Add your number below to receive a link.'
            )}
          </Text16>
          <PhoneFormField
            ref={phoneFieldRef}
            isSubmitted={isSubmitted}
            countryCode={countryCode}
            setCountryCode={setCountryCode}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            serverError={serverError}
          />
          <Column mTop={20}>
            <ProgressButton buttonType={ButtonType.primary} onClick={submit} height={42}>
              {gettext('Get the link ')}
            </ProgressButton>
            <Column top={20}>
              <SkipButton onClick={close}>
                <Text14 themeColor={ThemeColorType.gray} underline>
                  {gettext("I'll do this later")}
                </Text14>
              </SkipButton>
            </Column>
          </Column>
        </RightColumn>
      </Container>
    </Modal>
  );
};

export default GetMobileAppPopup;
